// cityServiceSlice.js
// Redux home for everything the Landing page's city flow needs:
//   1. checkCityService  - "is this address's city serviceable?" (LoyaltyController::checkCityService)
//   2. fetchTopServiceableCities - default "popular cities" suggestion list, store-backed
//   3. searchServiceableCities   - search-as-you-type suggestions, store-backed
// All three hit LoyaltyController endpoints that join store.city -> static_cities.id.
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { cityService } from "../../services/api";

// Auto-detect only: exact serviceability check for a resolved city.
export const checkCityService = createAsyncThunk(
    "cityService/check",
    async ({ city, state = "", country = "" }, { rejectWithValue }) => {
        try {
            return await cityService({ city, state, country });
        } catch (error) {
            return rejectWithValue(error?.message || "Something went wrong while checking city availability.");
        }
    }
);

export const fetchTopServiceableCities = createAsyncThunk(
    "cityService/fetchTop",
    async (limit = 5, { rejectWithValue }) => {
        try {
            const response = await cityService({ limit });
            return response?.data?.cities || [];
        } catch (error) {
            return rejectWithValue(error?.message || "Failed to load popular cities.");
        }
    }
);

export const searchServiceableCities = createAsyncThunk(
    "cityService/search",
    async ({ keyword, limit = 10 }, { rejectWithValue }) => {
        try {
            const response = await cityService({ keyword, limit });
            return response?.data?.cities || [];
        } catch (error) {
            return rejectWithValue(error?.message || "Failed to search cities.");
        }
    }
);

const initialState = {
    // single-city check (Landing's "is this address serviceable" flow)
    checking: false,
    notServiceable: null, // { city } | null
    matchedStore: null,
    matchedCity: null,
    checkError: null,

    // default "popular / serviceable cities" suggestion list
    topCities: [],
    topCitiesLoading: false,
    topCitiesError: null,

    // search-as-you-type suggestions sourced from store cities
    searchResults: [],
    searching: false,
    searchError: null,
};

const cityServiceSlice = createSlice({
    name: "cityService",
    initialState,
    reducers: {
        resetCityCheck(state) {
            state.notServiceable = null;
            state.matchedStore = null;
            state.matchedCity = null;
            state.checkError = null;
        },
        clearCitySearchResults(state) {
            state.searchResults = [];
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(checkCityService.pending, (state) => {
                state.checking = true;
                state.notServiceable = null;
                state.checkError = null;
            })
            .addCase(checkCityService.fulfilled, (state, action) => {
                state.checking = false;
                const data = action.payload?.data;
                if (data?.serviceable) {
                    state.matchedStore = data.store || null;
                    state.matchedCity = data.matchedCity || null;
                    state.notServiceable = null;
                } else {
                    state.notServiceable = { city: data?.city };
                    state.matchedStore = null;
                    state.matchedCity = null;
                }
            })
            .addCase(checkCityService.rejected, (state, action) => {
                // Don't block the user if the availability check itself fails.
                state.checking = false;
                state.checkError = action.payload;
            })

            .addCase(fetchTopServiceableCities.pending, (state) => {
                state.topCitiesLoading = true;
                state.topCitiesError = null;
            })
            .addCase(fetchTopServiceableCities.fulfilled, (state, action) => {
                state.topCitiesLoading = false;
                state.topCities = action.payload;
            })
            .addCase(fetchTopServiceableCities.rejected, (state, action) => {
                state.topCitiesLoading = false;
                state.topCitiesError = action.payload;
            })

            .addCase(searchServiceableCities.pending, (state) => {
                state.searching = true;
                state.searchError = null;
            })
            .addCase(searchServiceableCities.fulfilled, (state, action) => {
                state.searching = false;
                state.searchResults = action.payload;
            })
            .addCase(searchServiceableCities.rejected, (state, action) => {
                state.searching = false;
                state.searchError = action.payload;
            });
    },
});

export const { resetCityCheck, clearCitySearchResults } = cityServiceSlice.actions;
export default cityServiceSlice.reducer;